import { useState } from "react";
import useHttp from "./use_http";
import { useDispatch, useSelector } from "react-redux";
import {
  setCourses,
  setSelectedCourse,
} from "../features/community/community_slice";
import { createPostSchema } from "../utils/schema";
import { useFormik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";

const useCommunity = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const { httpService } = useHttp();

  const [loadings, setLoadings] = useState({
    getCourses: false,
    getPosts: false,
    getPostsPaginate: false,
    createPost: false,
    deletePost: false,
    getPostById: false,
    getPostReplies: false,
    getPostRepliesPaginates: false,
    deleteReply: false,
  });

  const [paginates, setPaginates] = useState({
    current: 1,
    has_more: false,
  });

  const [newPostModal, setNewPostModal] = useState(false);
  const [deletePostModal, setDeletePostModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(false);
  const [viewPostLikedByMe, setViewPostLikedByMe] = useState(false);
  const [viewPostLikes, setViewPostLikes] = useState(0);
  const [deleteReplyModal, setDeleteReplyModal] = useState(false);
  const [selectedReply, setSelectedReply] = useState(null);

  const selectedCourse = useSelector((state) => state.community.selectedCourse);
  const courses = useSelector((state) => state.community.courses);

  const [countsData, setCountsData] = useState([]);
  const [postsData, setPostsData] = useState([]);
  const [postRepliesData, setPostRepliesData] = useState([]);
  const [viewPostData, setViewPostData] = useState(null);

  const getCourses = async () => {
    try {
      setLoadings({ ...loadings, getCourses: true });
      const response = await httpService.get("course-comment/list/course");
      setLoadings({ ...loadings, getCourses: false });
      if (response.data) {
        let array = [];
        array = response.data?.filter((o) => o.course.status == 1);
        dispatch(setCourses(array));
        dispatch(setSelectedCourse(array[0]?.course));
      } else {
        dispatch(setCourses([]));
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, getCourses: false });
    }
  };

  const seenCounts = async (course_id) => {
    try {
      await httpService.get(`course-comment/seen`, {
        params: {
          courseId: course_id,
        },
      });

      const updatedCourses = courses.map((item) => {
        if (item.course.id === course_id) {
          return {
            ...item,
            unreadCount: 0,
          };
        }

        return item;
      });

      dispatch(setCourses(updatedCourses));
    } catch (error) {
      console.log(error);
    }
  };

  const getPosts = async (course_id, page) => {
    let array = [...postsData];
    let paramsData = {
      courseId: course_id,
      page: page,
      limit: 10,
      replyLimit: 2,
    };
    try {
      if (page == 1) {
        setLoadings({ ...loadings, getPosts: true });
      } else {
        setLoadings({ ...loadings, getPostsPaginate: true });
      }
      const response = await httpService.get(`course-comment`, {
        params: paramsData,
      });
      setLoadings({ ...loadings, getPosts: false, getPostsPaginate: false });
      setPaginates({
        current: response.data?.meta?.page,
        has_more:
          response.data?.meta?.page < response?.data?.meta?.pageCount
            ? true
            : false,
      });
      if (page == 1) {
        setPostsData(response.data?.comments);
      } else {
        response.data?.comments?.map((item) => {
          array.push(item);
        });
        setPostsData(array);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, getPosts: false, getPostsPaginate: false });
    }
  };

  const createPostController = useFormik({
    initialValues: {
      text: "",
      files: [],
    },
    validationSchema: createPostSchema,
    onSubmit: (values) => {
      createPost(values);
    },
  });

  const createPost = async (values) => {
    let postData = new FormData();
    postData.append("courseId", selectedCourse?.id);
    postData.append("text", values.text);
    if (values.files?.length > 0) {
      values?.files?.map((file) => {
        postData.append("comment", file?.file);
      });
    }
    try {
      setLoadings({ ...loadings, createPost: true });
      const response = await httpService.post("course-comment", postData);
      setLoadings({ ...loadings, createPost: false });
      if (response.status < 400) {
        setNewPostModal(false);
        setPostsData((prev) => [response.data.comment, ...prev]);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, createPost: false });
    }
  };

  const deletePost = async (post_id) => {
    try {
      setLoadings({ ...loadings, deletePost: true });
      const response = await httpService.delete(`course-comment/${post_id}`);
      setLoadings({ ...loadings, deletePost: false });
      if (response.status < 400) {
        setDeletePostModal(false);
        if (location.pathname?.includes("/community/")) {
          navigate("/community");
        } else {
          setPostsData(postsData.filter((o) => o.id != post_id));
        }
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, deletePost: false });
    }
  };

  const likePost = async (post) => {
    try {
      setPostsData((prevPosts) => {
        const next = [...prevPosts];
        const idx = next.findIndex((p) => p.id == post?.id);
        if (idx === -1) return prevPosts;

        next[idx] = {
          ...next[idx],
          likedByMe: !next[idx].likedByMe,
          likeCount:
            post?.likedByMe == true
              ? next[idx].likeCount - 1
              : next[idx].likeCount + 1,
        };
        return next;
      });
      const response = await httpService.put(
        `course-comment/${post?.id}/like`,
        {
          liked: !post?.likedByMe,
        },
      );
    } catch ({ err, response }) {
      setPostsData((prevPosts) => {
        const next = [...prevPosts];
        const idx = next.findIndex((p) => p.id == post?.id);
        if (idx === -1) return prevPosts;

        next[idx] = {
          ...next[idx],
          likedByMe: post?.likeCount,
        };
        return next;
      });
    }
  };

  const getPostById = async (post_id) => {
    try {
      setLoadings({ ...loadings, getPostById: true });
      const response = await httpService.get(`course-comment/${post_id}`);
      setLoadings({ ...loadings, getPostById: false });
      if (response.status < 400) {
        setViewPostData(response.data);
        setViewPostLikes(response.data.likeCount);
        setViewPostLikedByMe(response.data.likedByMe);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, getPostById: false });
    }
  };

  const likePostView = async () => {
    try {
      if (viewPostLikedByMe) {
        setViewPostLikes(viewPostLikes - 1);
      } else {
        setViewPostLikes(viewPostLikes + 1);
      }
      setViewPostLikedByMe(!viewPostLikedByMe);
      const response = await httpService.put(
        `course-comment/${viewPostData?.id}/like`,
        {
          liked: !viewPostData?.likedByMe,
        },
      );
    } catch ({ err, response }) {
      setViewPostLikes(viewPostLikes);
      setViewPostLikedByMe(viewPostLikedByMe);
    }
  };

  const getPostReplies = async (course_id, post_id, page) => {
    let array = [...postRepliesData];
    let paramsData = {
      courseId: course_id,
      rootId: post_id,
      page: page,
      limit: 10,
    };
    try {
      if (page == 1) {
        setLoadings({ ...loadings, getPostReplies: true });
      } else {
        setLoadings({ ...loadings, getPostRepliesPaginates: true });
      }
      const response = await httpService.get(`course-comment/replies`, {
        params: paramsData,
      });
      setLoadings({
        ...loadings,
        getPostReplies: false,
        getPostRepliesPaginates: false,
      });
      setPaginates({
        current: response.data?.meta?.page,
        has_more:
          response.data?.meta?.page < response?.data?.meta?.pageCount
            ? true
            : false,
      });
      if (page == 1) {
        setPostRepliesData(response.data?.comments);
      } else {
        response.data?.comments?.map((item) => {
          array.push(item);
        });
        setPostRepliesData(array);
      }
    } catch ({ err, response }) {
      setLoadings({
        ...loadings,
        getPostReplies: false,
        getPostRepliesPaginates: false,
      });
    }
  };

  const likeReply = async (reply) => {
    try {
      setPostRepliesData((prevReplies) => {
        const next = [...prevReplies];
        const idx = next.findIndex((p) => p.id == reply?.id);
        if (idx === -1) return prevReplies;

        next[idx] = {
          ...next[idx],
          likedByMe: !next[idx].likedByMe,
          likeCount:
            reply?.likedByMe == true
              ? next[idx].likeCount - 1
              : next[idx].likeCount + 1,
        };
        return next;
      });
      const response = await httpService.put(
        `course-comment/${reply?.id}/like`,
        {
          liked: !reply?.likedByMe,
        },
      );
    } catch ({ err, response }) {
      setPostsData((prevReplies) => {
        const next = [...prevReplies];
        const idx = next.findIndex((p) => p.id == reply?.id);
        if (idx === -1) return prevReplies;

        next[idx] = {
          ...next[idx],
          likedByMe: reply?.likeCount,
        };
        return next;
      });
    }
  };

  const deleteReply = async (reply_id) => {
    try {
      setLoadings({ ...loadings, deleteReply: true });
      const response = await httpService.delete(`course-comment/${reply_id}`);
      setLoadings({ ...loadings, deleteReply: false });
      if (response.status < 400) {
        setDeleteReplyModal(false);
        setPostRepliesData(postRepliesData.filter((o) => o.id != reply_id));
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, deleteReply: false });
    }
  };

  const createReplyController = useFormik({
    initialValues: {
      courseId: null,
      parentId: null,
      text: "",
      files: [],
    },
    validationSchema: createPostSchema,
    onSubmit: (values) => {
      createReply(values);
    },
  });

  const createReply = async (values) => {
    let postData = new FormData();
    postData.append("courseId", values?.courseId);
    postData.append("parentId", values?.parentId);
    postData.append("text", values.text);
    if (values.files?.length > 0) {
      values?.files?.map((file) => {
        postData.append("comment", file?.file);
      });
    }
    try {
      setLoadings({ ...loadings, createPost: true });
      const response = await httpService.post("course-comment", postData);
      setLoadings({ ...loadings, createPost: false });
      if (response.status < 400) {
        setNewPostModal(false);
        setPostRepliesData((prev) => [response.data.comment, ...prev]);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, createPost: false });
    }
  };

  const exports = {
    // entity methods
    getCourses,
    seenCounts,
    getPosts,
    deletePost,
    likePost,
    getPostById,
    likePostView,
    getPostReplies,
    likeReply,
    deleteReply,
    // controller methods
    createPostController,
    createReplyController,
    // entity states
    postsData,
    viewPostData,
    postRepliesData,
    // controller states
    paginates,
    newPostModal,
    setNewPostModal,
    deletePostModal,
    setDeletePostModal,
    selectedPost,
    setSelectedPost,
    viewPostLikedByMe,
    viewPostLikes,
    deleteReplyModal,
    setDeleteReplyModal,
    selectedReply,
    setSelectedReply,
    // loadings
    loadings,
  };
  return exports;
};

export default useCommunity;
